import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import TableRowForm from '../../../../components/table-row-form/table-row-form.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import CheckAccess from '../../../../components/check-access/check-access.component';
import Tooltip from '../../../../components/tooltip/tooltip.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import FilteredCell from '../../../../components/filtered-cell/filtered-cell.component';
import Loader from '../../../../components/loader/loader.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import IconBlockError from '../../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../../components/icon-block-success/icon-block-success.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldUpload from '../../../../components/form-field-upload/form-field-upload.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import RemoveIcon from '../../../../icons/remove-icon';
import DeleteIcon from '../../../../icons/delete-icon';
import AttachmentIcon from '../../../../icons/attachment-icon';
import FormatService from '../../../../services/format-service';
import StructureService from '../../../../services/structure-service';
import FormService from '../../../../services/form-service';
import useShiftState from '../../../../hooks/use-shift-state';
import useCurrentUserPermissions from '../../../../hooks/use-current-user-permissions';
import {
  DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW, DEAL_PARTICIPANT_APPROVAL_VIEW,
} from '../../../../constants/entity/permissions';
import PersonDefaultImage from '../../../../static/images/person-default-image.png';

const DealParticipantRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isInlineSaving,
    isNewEntity,
    tableName,
    dealParticipantData,
    dealParticipantGroupList,
    accessLevelList,
    contactPersonList,
    contactNotParticipantList,
    onDeleteClick,
    onContactChange,
    ndaFile,
    dealId,
    onNdaFileChange,
    onNdaFileDelete,
    tableColumnSettings,
    inputRefs,
    dealParticipantIndex,
    currentUser,
    dealParticipantNewItems,
    isSaving,
    onInlineSave,
    onInlineCancel,
    isSaveDisabled,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactNotParticipantSearch,
    onContactNotParticipantSearchClose,
  } = props;

  const isShiftPressed: boolean = useShiftState();
  const permissionList: string[] = useCurrentUserPermissions();

  const [isContactLinkedToUser, setIsContactLinkedToUser]: [boolean, Function] = useState(true);

  const dealParticipantAccessLevelViewPermissions: string[] = useMemo((): string[] => [
    DEAL_PARTICIPANT_ACCESS_LEVEL_VIEW,
  ], []);
  const isApprovalViewEnabled: boolean = StructureService.checkPermission(
    DEAL_PARTICIPANT_APPROVAL_VIEW, permissionList,
  );

  const dealParticipantId: number = (dealParticipantData && dealParticipantData.id) || FormService.FORM_KEY_NEW;
  const formKeyDealParticipant: string = FormService.getIndexedFormFieldKey(dealParticipantId);
  const formPrefix: string = `dealData.dealParticipantsData[${formKeyDealParticipant}]`;

  const menuItemDealParticipantGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemLabelKey: 'name',
    optionList: dealParticipantGroupList,
  }), [dealParticipantGroupList]);

  const menuItemContactPersonList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: contactPersonList,
    defaultImage: PersonDefaultImage,
  }), [contactPersonList]);

  const menuItemContactNotParticipantList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionItemExtraLabelKey: 'email',
    optionList: contactNotParticipantList,
    defaultImage: PersonDefaultImage,
  }), [contactNotParticipantList]);

  const menuItemAccessLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: isContactLinkedToUser ? accessLevelList : [],
  }), [accessLevelList, isContactLinkedToUser]);

  const contactChange: Function = useCallback((contactId: number): void => {
    const contact: Contact = contactPersonList && contactPersonList.length && contactId
      ? contactPersonList.find((contactItem: Contact): boolean => contactItem.id === contactId) : null;
    const isSelectedContactLinkedToUser: boolean = contact && !!contact.user;

    if (contact) {
      setIsContactLinkedToUser(isSelectedContactLinkedToUser);
    }

    if (onContactChange) {
      onContactChange(dealParticipantId, contact, isSelectedContactLinkedToUser);
    }
  }, [dealParticipantId, contactPersonList]);

  const ndaFileChange: Function = useCallback((file: File): void => {
    if (onNdaFileChange) {
      onNdaFileChange(dealParticipantId, file);
    }
  }, [dealParticipantId]);

  const ndaFileDelete: MouseEventHandlerFunction = useCallback((): void => {
    if (onNdaFileDelete) {
      onNdaFileDelete(dealParticipantId);
    }
  }, [dealParticipantId]);

  const tableDealParticipantListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'group.title',
      component: (field: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = dealParticipantGroupList && !dealParticipantGroupList.length;

        return (
          <TableCell>
            <FormFieldText isHidden name={`${formPrefix}.id`} />
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.groupId`}
              optionsTotal={dealParticipantGroupList && dealParticipantGroupList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealParticipantIndex}.${fieldIndex}` : null}
            >
              {menuItemDealParticipantGroupList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 2,
      name: 'contact',
      component: (field: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = isNewEntity
          ? contactNotParticipantList && !contactNotParticipantList.length
          : contactPersonList && !contactPersonList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withSearch
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.contactId`}
              searchOptions={isNewEntity ? {
                dealId: dealId || null,
              } : undefined}
              optionsTotal={isNewEntity ? contactNotParticipantList && contactNotParticipantList.length
                : contactPersonList && contactPersonList.length}
              onSearch={isNewEntity ? onContactNotParticipantSearch : onContactPersonSearch}
              onClose={isNewEntity ? onContactNotParticipantSearchClose : onContactPersonSearchClose}
              onChange={(contactId: number): void => {
                contactChange(contactId);
              }}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${dealParticipantIndex}.${fieldIndex}` : null}
            >
              {isNewEntity ? menuItemContactNotParticipantList : menuItemContactPersonList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 3,
      name: 'contact.fullName',
      component: (): ReactElement => (
        <TableCell isText isSticky isStickyLeft isDisabled>
          {dealParticipantData.contact && dealParticipantData.contact.fullName}
        </TableCell>
      ),
    },
    {
      id: 4,
      name: 'contact.company.name',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealParticipantData.contact && dealParticipantData.contact.company
            && dealParticipantData.contact.company.name}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'contact.email',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealParticipantData.contact && dealParticipantData.contact.email}
        </TableCell>
      ),
    },
    {
      id: 6,
      name: 'contact.phone',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealParticipantData.contact && dealParticipantData.contact.phone}
        </TableCell>
      ),
    },
    {
      id: 7,
      name: 'name',
      component: (field: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.name`}
            placeholder="Title"
            inputRefs={inputRefs}
            inputRefIndex={`${dealParticipantIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'createdDate',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {dealParticipantData.createdDate}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'accessLevel.title',
      component: (field: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = !isContactLinkedToUser || (accessLevelList && !accessLevelList.length);

        return (
          <CheckAccess permissions={dealParticipantAccessLevelViewPermissions}>
            <TableCell>
              <FormFieldSelect
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                name={`${formPrefix}.accessLevel`}
                optionsTotal={accessLevelList && accessLevelList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${dealParticipantIndex}.${fieldIndex}` : null}
              >
                {menuItemAccessLevelList}
              </FormFieldSelect>
            </TableCell>
          </CheckAccess>
        );
      },
    },
    {
      id: 10,
      name: 'isApproved',
      component: (): ReactElement => (
        <TableCell isDisabled={!isApprovalViewEnabled} isTextCenter={isApprovalViewEnabled}>
          {
            isApprovalViewEnabled ? (
              <FormFieldCheckbox name={`${formPrefix}.isApproved`} />
            ) : FormatService.formatBooleanValue(dealParticipantData.isApproved)
          }
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'ndaFile',
      permission: DEAL_PARTICIPANT_APPROVAL_VIEW,
      component: (): ReactElement => (
        <TableCell isText>
          <FlexContainer>
            {
              !ndaFile && ndaFile !== null && dealParticipantData.ndaFile && (
                <FlexContainer>
                  <PaddedFlexContainer>
                    <AttachmentIcon />
                  </PaddedFlexContainer>
                  <TextBlock>
                    {dealParticipantData.ndaFile.name}
                  </TextBlock>
                  <ActionButton isActive isInline withNoHighlight withSmallPadding onClick={ndaFileDelete}>
                    <DeleteIcon />
                  </ActionButton>
                </FlexContainer>
              )
            }
            <FormFieldUpload
              isInline
              uploadFile={ndaFile}
              name={`${formPrefix}.ndaFile`}
              buttonLabel="Upload New File"
              onFileChange={ndaFileChange}
              onFileDelete={ndaFileDelete}
            />
          </FlexContainer>
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'updatedDate',
      component: (): ReactElement => (
        <TableCell isText isDisabled />
      ),
    },
    {
      id: 13,
      name: 'actions',
      isNotFiltered: true,
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            !isNewEntity && isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isNewEntity && !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(dealParticipantData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isNewEntity && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(dealParticipantData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
          {
            !isSaving && isNewEntity && (
              <Tooltip title="Delete">
                <ActionButton isInline withSmallPadding onClick={onDeleteClick}>
                  <RemoveIcon />
                </ActionButton>
              </Tooltip>
            )
          }
        </TableCell>
      ),
    },
  ], [
    isShiftPressed, isContactLinkedToUser, dealParticipantNewItems, isSaveDisabled, isSaving, ndaFile,
    dealParticipantData, contactPersonList, contactNotParticipantList, isNewEntity, isApprovalViewEnabled,
  ]);

  useEffect((): void => {
    if (isNewEntity || (dealParticipantData && (!dealParticipantData.contact
      || !dealParticipantData.contact.user))) {
      setIsContactLinkedToUser(false);
    }
  }, [dealParticipantData]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableDealParticipantListFields && !!tableDealParticipantListFields.length
        && StructureService.extendOptions(tableDealParticipantListFields, tableColumnSettings, true)
          .map((tableField: TableField, tableFieldIndex: number): ReactElement | boolean => (
            !!tableField.component && (
              !tableField.isNotFiltered ? (
                <CheckAccess key={tableField.id} permissions={tableField.permission ? [tableField.permission] : []}>
                  <FilteredCell
                    tableName={tableName}
                    columnName={tableField.name}
                    currentUser={currentUser}
                  >
                    {tableField.component(tableField, tableFieldIndex)}
                  </FilteredCell>
                </CheckAccess>
              ) : (
                <Fragment key={tableField.id}>
                  {tableField.component(tableField, tableFieldIndex)}
                </Fragment>
              )
            )
          ))
      }
    </TableRowForm>
  );
});

export default DealParticipantRowForm;
